'use client';
import { Fragment } from 'react';
import { ProductFillFrames } from '@/app/components/icons/ProductFillFrames';
import { ProductLineAiVideo } from '@/app/components/icons/ProductLineAiVideo';
import { MediaWithMaskBg } from '@/app/components/shared/MediaWithMaskBg';
import type { TaskInputPrompts } from '@/app/types';
import { cn } from '@/app/utils';

const processExtendInputPrompts = (prompts: TaskInputPrompts[]) => {
  const items: Array<{ type: 'video'; src?: string; coverSrc?: string } | { type: 'image'; src: string }> = [];
  // biome-ignore lint/suspicious/useIterableCallbackReturn: this map is used for side effects to build ordered display items
  prompts.map((prompt: TaskInputPrompts) => {
    if (prompt.type === 'video') {
      const coverSrc = prompt.src_img || prompt.cover_uri;
      const videoSrc = prompt.content;
      if (!coverSrc && !videoSrc) {
        return;
      }
      const item = {
        type: 'video' as const,
        src: videoSrc,
        coverSrc,
      };
      if (items.length) {
        items.unshift(item);
      } else {
        items.push(item);
      }
    }
    if (prompt.type === 'image' && prompt.content) {
      items.push({
        type: 'image' as const,
        src: prompt.content,
      });
    }
  });
  return items;
};

interface ExtendPromptsDisplayProps {
  prompts: TaskInputPrompts[];
  className?: string;
}

export const hasExtendDisplayPrompts = (prompts: TaskInputPrompts[]): boolean =>
  processExtendInputPrompts(prompts).length > 0;

export const ExtendPromptsDisplay: React.FC<ExtendPromptsDisplayProps> = ({ prompts, className }) => {
  const displayItems = processExtendInputPrompts(prompts);

  if (!displayItems.length) {
    return null;
  }

  return (
    <div className={cn('relative flex w-fit items-center gap-[9px]', className)}>
      {displayItems.map((image, idx) => (
        <Fragment key={`${image.type}-${idx}`}>
          <div className="relative size-[42px] overflow-hidden rounded-[3px]">
            {image.type === 'video' ? (
              <MediaWithMaskBg
                src={image.src || image.coverSrc}
                maskSrc={image.coverSrc}
                videoCoverImage={image.coverSrc}
                type={image.src ? 'video' : 'image'}
                videoAutoPlay={false}
                className="h-full w-full rounded-8"
                imgClassName="object-cover"
                disableDrag={true}
              />
            ) : (
              <img src={image.src} alt={image.type} className="h-full w-full rounded-8 object-cover" />
            )}
            {image.type === 'video' && (
              <ProductLineAiVideo className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 size-5 text-system-white" />
            )}
          </div>

          {idx < displayItems.length - 1 && (
            <ProductFillFrames className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 z-10 text-base" />
          )}
        </Fragment>
      ))}
    </div>
  );
};
